import { useState } from 'react';

import { useParams } from 'react-router-dom';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useBulkDeleteFacts } from '@/api/mutations/useBulkDeleteFacts';
import { useIndexedDb } from '@/hooks/useIndexedDb';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { InfoCard } from '@/new/components/info-card';
import { Button } from '@/new/components/shadcn/button';
import { DropdownMenuItem } from '@/new/components/shadcn/dropdown-menu';

export function ArchiveSelectedButton({
  selectedFactIds,
  setSelectedFactIds,
  asMenuItem = false,
  onSuccess,
}: {
  selectedFactIds: Set<string>;
  setSelectedFactIds: (ids: Set<string>) => void;
  asMenuItem?: boolean;
  onSuccess?: () => void;
}) {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const [open, setOpen] = useState(false);

  const { getAllSelectedFactIds, deselectMultipleFacts } = useIndexedDb();
  const { mutate: deleteFacts, isLoading: isDeletingFacts } = useBulkDeleteFacts(matterId, subsetId);

  const handleDeleteAll = async () => {
    const factIds = await getAllSelectedFactIds(subsetId);
    trackCustomEvent(TrackingEventsV2.FactBulkDelete, { subsetId, count: factIds.length });
    deleteFacts(
      { factIds },
      {
        onSuccess: () => {
          deselectMultipleFacts(subsetId, factIds);
          setSelectedFactIds(new Set());
          setOpen(false);
          onSuccess?.();
        },
      },
    );
  };

  const label = `Archive ${selectedFactIds.size > 0 ? `${selectedFactIds.size} ` : ''}Fact${
    selectedFactIds.size === 1 ? '' : 's'
  }`;

  return (
    <>
      {asMenuItem ? (
        <DropdownMenuItem disabled={selectedFactIds.size === 0} onClick={() => setOpen(true)}>
          <Icon className="lucide--archive" />
          {label}
        </DropdownMenuItem>
      ) : (
        <Button onClick={() => setOpen(!open)} disabled={selectedFactIds.size === 0}>
          <Icon className="lucide--archive" />
          {label}
        </Button>
      )}
      <ConfirmationModal
        isOpen={open}
        onOpenChange={setOpen}
        title="Archive Facts"
        onConfirm={handleDeleteAll}
        isLoading={isDeletingFacts}
      >
        <InfoCard className="mb-4 text-sm text-gray-600">
          <p>Large archives may take a moment to appear in the facts table.</p>
        </InfoCard>
        <p className="mt-3">
          Are you sure you want to archive all selected facts? This will prevent these facts from appearing in any
          subset.
        </p>
        <p className="mt-3">Note that any references to these facts will still exist.</p>
      </ConfirmationModal>
    </>
  );
}
